/**
 * Equipo — `OD-0060` D-03 a D-06 y la enmienda E-01 de `TDS-FREE-DOWNGRADE` §3.
 *
 * En Configuración, sólo para quien tiene `users.manage` (OWNER y ADMIN):
 * quién entra en la empresa, las invitaciones pendientes y cuántos usuarios
 * quedan libres.
 *
 * ```text
 * ASIENTOS     los cuenta la API (D-03): personas con acceso + invitaciones
 *              pendientes. Aqui no se suma nada
 * QUE SE PUEDE lo dice la API por persona (`puede`), con las reglas de D-06:
 *              el OWNER no se toca, un ADMIN no toca a otro ADMIN ni a si
 *              mismo. La API lo vuelve a comprobar en cada accion
 * QUITAR       pide confirmacion en el mismo sitio: no se borra nada, pero
 *              esa persona deja de entrar en el acto
 * ```
 *
 * COMPRAR UN USUARIO (D-05): sin ninguno libre, y si la API dice que quien
 * mira puede (`puedeComprarAsiento`), se ofrece uno más. Primero el precio,
 * que la API lee de Stripe; después la confirmación, porque el prorrateo se
 * cobra en el acto. El usuario nuevo aparece libre cuando el pago se confirma,
 * así que tras comprar se relee el equipo un par de veces.
 *
 * QUITAR UN USUARIO DEL PLAN (enmienda de D-05 del 2026-10-26): solo uno
 * LIBRE, y si la API dice que quien mira puede (`puedeQuitarAsiento`). Se
 * aplica en el acto y sin abono, así que la confirmación lo dice antes.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  apiEquipo,
  ESTADO_VALIDACION,
  ROLES_DEL_EQUIPO,
  type Equipo,
  type InvitacionDelEquipo,
  type MiembroDelEquipo,
  type OfertaDeAsiento,
  type Resultado,
  type RolDelEquipo,
} from "../api/cliente";
import { t } from "../i18n/es";
import { diaLegible, importeLegible } from "../ui/formato";
import { Aviso, Boton, Campo, Selector, Tarjeta } from "../ui/primitivas";

type Carga = { readonly tipo: "cargando" } | { readonly tipo: "error" } | { readonly tipo: "datos"; readonly equipo: Equipo };

type Mensaje = { readonly tono: "exito" | "error" | "aviso"; readonly texto: string };

/** Una llamada a la API. Firma de llamada y no flecha: ver `check:web-guardrails`, que lee `=> Promise<` como texto. */
interface Peticion<T> {
  (): Promise<Resultado<T>>;
}

const OPCIONES_DE_ROL = ROLES_DEL_EQUIPO.map((r) => ({ valor: r, texto: t.equipo.roles[r] ?? r }));

const nombreDeRol = (rol: string): string => t.equipo.roles[rol] ?? rol;
const hecho = (texto: string) => (): Mensaje => ({ tono: "exito", texto });
const quien = (m: MiembroDelEquipo): string => m.email ?? t.equipo.sinCorreo;
const esRolDelEquipo = (rol: string): rol is RolDelEquipo => (ROLES_DEL_EQUIPO as readonly string[]).includes(rol);

/** El texto de un rechazo: el de la API si lo trae, que es el que sabe por qué. */
function textoDeFallo(r: Resultado<unknown>): string {
  if (r.ok) return "";
  if (r.estado === 0) return t.comun.sinRed;
  if (r.estado === 429) return t.comun.demasiadosIntentos;
  if (r.error === null) return t.comun.errorGenerico;
  return r.error.action === null ? r.error.message : `${r.error.message} ${r.error.action}`;
}

export function PantallaEquipo({
  hayConexion,
  onSesionCaducada,
  onIrAPlan,
}: {
  hayConexion: boolean;
  onSesionCaducada: () => void;
  onIrAPlan: () => void;
}) {
  const [carga, setCarga] = useState<Carga>({ tipo: "cargando" });
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  /** El id de la fila con una acción en marcha: mientras, nada más se pulsa. */
  const [ocupado, setOcupado] = useState<string | null>(null);

  const cargar = useCallback(async (): Promise<void> => {
    const r = await apiEquipo.ver();
    if (r.ok) {
      setCarga({ tipo: "datos", equipo: r.datos });
      return;
    }
    if (r.estado === 401) onSesionCaducada();
    else setCarga({ tipo: "error" });
  }, [onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /** Tras comprar o quitar: el cambio llega con el webhook, unos segundos después. */
  const relecturas = useRef<number[]>([]);
  useEffect(() => () => relecturas.current.forEach((id) => window.clearTimeout(id)), []);
  function releerLuego(): void {
    for (const ms of [3000, 10000]) relecturas.current.push(window.setTimeout(() => void cargar(), ms));
  }

  /** Una acción sobre una fila: al terminar se relee el equipo, que es lo que cuenta. */
  async function hacer<T>(
    id: string,
    accion: Peticion<T>,
    exito: (datos: T) => Mensaje,
  ): Promise<void> {
    setOcupado(id);
    setMensaje(null);
    const r = await accion();
    if (!r.ok && r.estado === 401) {
      onSesionCaducada();
      return;
    }
    setMensaje(r.ok ? exito(r.datos) : { tono: "error", texto: textoDeFallo(r) });
    await cargar();
    setOcupado(null);
  }

  if (carga.tipo === "cargando") {
    return (
      <Tarjeta titulo={t.equipo.titulo}>
        <p>{t.equipo.cargando}</p>
      </Tarjeta>
    );
  }
  if (carga.tipo === "error") {
    return (
      <Tarjeta titulo={t.equipo.titulo}>
        <Aviso tono="error">{t.equipo.noSePuedeVer}</Aviso>
        <Boton variante="secundario" onClick={() => void cargar()}>
          {t.equipo.reintentar}
        </Boton>
      </Tarjeta>
    );
  }

  const { asientos, miembros, invitaciones } = carga.equipo;
  const bloqueado = ocupado !== null || !hayConexion;

  return (
    <Tarjeta titulo={t.equipo.titulo}>
      <section aria-labelledby="titulo-asientos" className="equipo-asientos">
        <h3 id="titulo-asientos" className="subtitulo">
          {t.equipo.asientos(asientos.ocupados, asientos.maximo)}
        </h3>
        {asientos.admiteAdicionales ? (
          <>
            <p>
              {asientos.libres > 0
                ? t.equipo.libres(asientos.libres)
                : asientos.maximo >= asientos.techo
                  ? t.equipo.enElTecho(asientos.techo)
                  : t.equipo.sinLibres}
            </p>
            <p className="ayuda-neutra">{t.equipo.asientosDetalle}</p>
            {carga.equipo.puedeQuitarAsiento ? (
              <QuitarAsiento
                quedan={asientos.maximo - 1}
                bloqueado={bloqueado}
                onSesionCaducada={onSesionCaducada}
                onQuitado={(m) => {
                  setMensaje(m);
                  void cargar();
                  releerLuego();
                }}
              />
            ) : null}
          </>
        ) : (
          <>
            <p>{t.equipo.soloUnaPersona}</p>
            <Boton variante="secundario" onClick={onIrAPlan}>
              {t.equipo.irAPlan}
            </Boton>
          </>
        )}
      </section>

      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
      {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}

      <section aria-labelledby="titulo-miembros">
        <h3 id="titulo-miembros" className="subtitulo">
          {t.equipo.miembros}
        </h3>
        <ul className="lista-equipo">
          {miembros.map((m) => (
            <FilaMiembro
              key={m.id}
              miembro={m}
              bloqueado={bloqueado}
              enMarcha={ocupado === m.id}
              hayLibres={asientos.libres > 0}
              onCambiarRol={(rol) => void hacer(m.id, () => apiEquipo.cambiarRol(m.id, rol), hecho(t.equipo.rolCambiado))}
              onQuitar={() => void hacer(m.id, () => apiEquipo.quitar(m.id), hecho(t.equipo.quitado))}
              onReactivar={() => void hacer(m.id, () => apiEquipo.reactivar(m.id), hecho(t.equipo.reactivado))}
            />
          ))}
        </ul>
      </section>

      <section aria-labelledby="titulo-invitaciones">
        <h3 id="titulo-invitaciones" className="subtitulo">
          {t.equipo.invitaciones}
        </h3>
        {invitaciones.length === 0 ? (
          <p className="ayuda-neutra">{t.equipo.sinInvitaciones}</p>
        ) : (
          <ul className="lista-equipo">
            {invitaciones.map((i) => (
              <FilaInvitacion
                key={i.id}
                invitacion={i}
                bloqueado={bloqueado}
                enMarcha={ocupado === i.id}
                onReenviar={() =>
                  void hacer(i.id, () => apiEquipo.reenviar(i.id), (d) =>
                    d.correoEnviado ? { tono: "exito", texto: t.equipo.reenviada } : { tono: "aviso", texto: t.equipo.correoNoSalio },
                  )
                }
                onRevocar={() => void hacer(i.id, () => apiEquipo.revocar(i.id), hecho(t.equipo.revocada))}
              />
            ))}
          </ul>
        )}
      </section>

      {asientos.admiteAdicionales ? (
        <FormularioInvitar
          hayConexion={hayConexion}
          hayLibres={asientos.libres > 0}
          puedeComprar={carga.equipo.puedeComprarAsiento}
          bloqueado={ocupado !== null}
          onSesionCaducada={onSesionCaducada}
          onInvitada={(m) => {
            setMensaje(m);
            void cargar();
          }}
          onComprado={(m) => {
            setMensaje(m);
            void cargar();
            releerLuego();
          }}
        />
      ) : null}
    </Tarjeta>
  );
}

function FilaMiembro({
  miembro: m,
  bloqueado,
  enMarcha,
  hayLibres,
  onCambiarRol,
  onQuitar,
  onReactivar,
}: {
  miembro: MiembroDelEquipo;
  bloqueado: boolean;
  enMarcha: boolean;
  hayLibres: boolean;
  onCambiarRol: (rol: RolDelEquipo) => void;
  onQuitar: () => void;
  onReactivar: () => void;
}) {
  const [rol, setRol] = useState<RolDelEquipo | null>(esRolDelEquipo(m.rol) ? m.rol : null);
  const [confirmando, setConfirmando] = useState(false);
  const idRol = `rol-${m.id}`;

  return (
    <li className="fila-equipo">
      <div className="fila-equipo-quien">
        <strong>{quien(m)}</strong>
        {m.eresTu ? <span className="insignia insignia-equipo">{t.equipo.tu}</span> : null}
        <span className="ayuda-neutra">{nombreDeRol(m.rol)}</span>
        {m.desactivadoPorPlan ? <span className="ayuda-neutra">{t.equipo.desactivado}</span> : null}
      </div>

      {m.puede.cambiarRol && rol !== null ? (
        <div className="fila-equipo-rol">
          <Selector
            id={idRol}
            etiqueta={t.equipo.rolDe(quien(m))}
            valor={rol}
            opciones={OPCIONES_DE_ROL}
            onChange={setRol}
            deshabilitado={bloqueado}
          />
          <p className="ayuda-neutra">{t.equipo.rolesAyuda[rol]}</p>
          {rol !== m.rol ? (
            <Boton variante="secundario" deshabilitado={bloqueado} onClick={() => onCambiarRol(rol)}>
              {enMarcha ? t.equipo.cambiandoRol : t.equipo.cambiarRol}
            </Boton>
          ) : null}
        </div>
      ) : null}

      <div className="acciones-en-linea">
        {m.desactivadoPorPlan && m.puede.reactivar ? (
          <Boton variante="secundario" deshabilitado={bloqueado} onClick={onReactivar}>
            {enMarcha ? t.equipo.reactivando : t.equipo.reactivar}
          </Boton>
        ) : null}
        {m.desactivadoPorPlan && !m.puede.reactivar && !hayLibres && m.puede.quitar ? (
          <p className="ayuda-neutra">{t.equipo.reactivarSinAsiento}</p>
        ) : null}
        {m.puede.quitar && !confirmando ? (
          <Boton variante="terciario" deshabilitado={bloqueado} onClick={() => setConfirmando(true)}>
            {enMarcha ? t.equipo.quitando : t.equipo.quitar}
          </Boton>
        ) : null}
      </div>

      {m.puede.quitar && confirmando ? (
        <div className="fila-equipo-confirmar">
          <p>{t.equipo.quitarDetalle}</p>
          <div className="acciones-en-linea">
            <Boton
              variante="peligro"
              deshabilitado={bloqueado}
              onClick={() => {
                // El resultado sale arriba; si sale bien, la fila ya no está.
                setConfirmando(false);
                onQuitar();
              }}
            >
              {t.equipo.confirmarQuitar(quien(m))}
            </Boton>
            <Boton variante="secundario" deshabilitado={bloqueado} onClick={() => setConfirmando(false)}>
              {t.equipo.cancelar}
            </Boton>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function FilaInvitacion({
  invitacion: i,
  bloqueado,
  enMarcha,
  onReenviar,
  onRevocar,
}: {
  invitacion: InvitacionDelEquipo;
  bloqueado: boolean;
  enMarcha: boolean;
  onReenviar: () => void;
  onRevocar: () => void;
}) {
  return (
    <li className="fila-equipo">
      <div className="fila-equipo-quien">
        <strong>{i.email}</strong>
        <span className="ayuda-neutra">{nombreDeRol(i.rol)}</span>
        <span className="ayuda-neutra">{t.equipo.caduca(diaLegible(i.caduca.slice(0, 10)))}</span>
      </div>
      <div className="acciones-en-linea">
        {i.puedeReenviar ? (
          <Boton variante="secundario" deshabilitado={bloqueado} onClick={onReenviar}>
            {enMarcha ? t.equipo.reenviando : t.equipo.reenviar}
          </Boton>
        ) : (
          <span className="ayuda-neutra">{t.equipo.yaReenviada}</span>
        )}
        <Boton variante="terciario" deshabilitado={bloqueado} onClick={onRevocar}>
          {enMarcha ? t.equipo.revocando : t.equipo.revocar}
        </Boton>
      </div>
    </li>
  );
}

function FormularioInvitar({
  hayConexion,
  hayLibres,
  puedeComprar,
  bloqueado,
  onSesionCaducada,
  onInvitada,
  onComprado,
}: {
  hayConexion: boolean;
  hayLibres: boolean;
  puedeComprar: boolean;
  bloqueado: boolean;
  onSesionCaducada: () => void;
  onInvitada: (m: Mensaje) => void;
  onComprado: (m: Mensaje) => void;
}) {
  const [email, setEmail] = useState("");
  const [rol, setRol] = useState<RolDelEquipo>("SALES");
  const [errorEmail, setErrorEmail] = useState<string | null>(null);
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function invitar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErrorGeneral(null);
    if (email.trim() === "") {
      setErrorEmail(t.equipo.faltaEmail);
      return;
    }
    setErrorEmail(null);
    setEnviando(true);
    const r = await apiEquipo.invitar(email.trim(), rol);
    setEnviando(false);
    if (r.ok) {
      onInvitada(
        r.datos.correoEnviado
          ? { tono: "exito", texto: t.equipo.invitada(email.trim()) }
          : { tono: "aviso", texto: t.equipo.correoNoSalio },
      );
      setEmail("");
      return;
    }
    if (r.estado === 401) onSesionCaducada();
    else if (r.estado === ESTADO_VALIDACION && r.error !== null && r.error.detalles.field === "email") setErrorEmail(r.error.message);
    else setErrorGeneral(textoDeFallo(r));
  }

  return (
    <section aria-labelledby="titulo-invitar">
      <h3 id="titulo-invitar" className="subtitulo">
        {t.equipo.invitar}
      </h3>
      {hayLibres ? (
        <form onSubmit={invitar} noValidate>
          <p className="ayuda-neutra">{t.equipo.invitarDetalle}</p>
          <Campo
            id="equipo-email"
            etiqueta={t.equipo.email}
            tipo="email"
            valor={email}
            onChange={setEmail}
            autoComplete="off"
            error={errorEmail}
          />
          <Selector id="equipo-rol" etiqueta={t.equipo.rol} valor={rol} opciones={OPCIONES_DE_ROL} onChange={setRol} />
          <p className="ayuda-neutra">{t.equipo.rolesAyuda[rol]}</p>
          {errorGeneral !== null ? <Aviso tono="error">{errorGeneral}</Aviso> : null}
          <Boton tipo="submit" deshabilitado={enviando || bloqueado || !hayConexion}>
            {enviando ? t.equipo.enviando : t.equipo.enviarInvitacion}
          </Boton>
        </form>
      ) : (
        <>
          <p>{t.equipo.sinLibres}</p>
          {puedeComprar ? (
            <ComprarAsiento
              hayConexion={hayConexion}
              bloqueado={bloqueado}
              onSesionCaducada={onSesionCaducada}
              onComprado={onComprado}
            />
          ) : null}
        </>
      )}
    </section>
  );
}

/** La enmienda de D-05: un usuario libre menos, con lo que significa a la vista antes. */
function QuitarAsiento({
  quedan,
  bloqueado,
  onSesionCaducada,
  onQuitado,
}: {
  quedan: number;
  bloqueado: boolean;
  onSesionCaducada: () => void;
  onQuitado: (m: Mensaje) => void;
}) {
  const [paso, setPaso] = useState<"boton" | "confirmando" | "quitando">("boton");
  const [error, setError] = useState<string | null>(null);

  async function quitar(): Promise<void> {
    setError(null);
    setPaso("quitando");
    const r = await apiEquipo.quitarAsiento();
    if (r.ok) {
      setPaso("boton");
      onQuitado({ tono: "exito", texto: t.equipo.asientoQuitado });
      return;
    }
    setPaso("confirmando");
    if (r.estado === 401) onSesionCaducada();
    else setError(textoDeFallo(r));
  }

  if (paso === "boton") {
    return (
      <Boton variante="secundario" onClick={() => setPaso("confirmando")} deshabilitado={bloqueado}>
        {t.equipo.quitarAsiento}
      </Boton>
    );
  }
  const quitando = paso === "quitando";
  return (
    <div className="equipo-compra">
      <p>{t.equipo.quitarAsientoAviso(quedan)}</p>
      {error !== null ? <Aviso tono="error">{error}</Aviso> : null}
      <div className="acciones-en-linea">
        <Boton onClick={() => void quitar()} deshabilitado={quitando || bloqueado}>
          {quitando ? t.equipo.quitandoAsiento : t.equipo.confirmarQuitarAsiento}
        </Boton>
        <Boton variante="secundario" onClick={() => setPaso("boton")} deshabilitado={quitando}>
          {t.equipo.cancelar}
        </Boton>
      </div>
    </div>
  );
}

type PasoDeCompra =
  | { readonly tipo: "boton" }
  | { readonly tipo: "consultando" }
  | { readonly tipo: "oferta"; readonly oferta: OfertaDeAsiento }
  | { readonly tipo: "comprando"; readonly oferta: OfertaDeAsiento };

/** La renovación, en el día de Madrid: Stripe la da en UTC y a las 23:30 UTC ya es el día siguiente aquí. */
function diaDeMadrid(iso: string): string {
  return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Madrid" });
}

/** D-05: un usuario más, con su precio a la vista antes de pagar. */
function ComprarAsiento({
  hayConexion,
  bloqueado,
  onSesionCaducada,
  onComprado,
}: {
  hayConexion: boolean;
  bloqueado: boolean;
  onSesionCaducada: () => void;
  onComprado: (m: Mensaje) => void;
}) {
  const [paso, setPaso] = useState<PasoDeCompra>({ tipo: "boton" });
  const [error, setError] = useState<string | null>(null);

  async function consultar(): Promise<void> {
    setError(null);
    setPaso({ tipo: "consultando" });
    const r = await apiEquipo.ofertaAsiento();
    if (r.ok) {
      setPaso({ tipo: "oferta", oferta: r.datos });
      return;
    }
    setPaso({ tipo: "boton" });
    if (r.estado === 401) onSesionCaducada();
    else setError(textoDeFallo(r));
  }

  async function comprar(oferta: OfertaDeAsiento): Promise<void> {
    setError(null);
    setPaso({ tipo: "comprando", oferta });
    const r = await apiEquipo.comprarAsiento();
    if (r.ok) {
      setPaso({ tipo: "boton" });
      onComprado(r.datos.pagoPendiente ? { tono: "aviso", texto: t.equipo.pagoPendiente } : { tono: "exito", texto: t.equipo.comprado });
      return;
    }
    setPaso({ tipo: "oferta", oferta });
    if (r.estado === 401) onSesionCaducada();
    else setError(textoDeFallo(r));
  }

  const aviso = error !== null ? <Aviso tono="error">{error}</Aviso> : null;
  if (paso.tipo === "boton" || paso.tipo === "consultando") {
    return (
      <>
        {aviso}
        <Boton variante="secundario" onClick={() => void consultar()} deshabilitado={paso.tipo === "consultando" || bloqueado || !hayConexion}>
          {paso.tipo === "consultando" ? t.equipo.consultandoPrecio : t.equipo.comprar}
        </Boton>
      </>
    );
  }
  const { oferta } = paso;
  const comprando = paso.tipo === "comprando";
  return (
    <div className="equipo-compra">
      <p>{t.equipo.oferta(importeLegible(oferta.precio))}</p>
      <p className="ayuda-neutra">{t.equipo.ofertaHoy(oferta.renueva === null ? null : diaDeMadrid(oferta.renueva))}</p>
      {aviso}
      <div className="acciones-en-linea">
        <Boton onClick={() => void comprar(oferta)} deshabilitado={comprando || bloqueado || !hayConexion}>
          {comprando ? t.equipo.comprando : t.equipo.confirmarCompra}
        </Boton>
        <Boton variante="secundario" onClick={() => setPaso({ tipo: "boton" })} deshabilitado={comprando}>
          {t.equipo.cancelar}
        </Boton>
      </div>
    </div>
  );
}
